/**
 * PerformanceSummary component displaying key performance statistics in a responsive grid.
 */

import { formatCurrency, formatPercent } from '@/utils/formatters';
import type { PerformanceStats } from '@/types/performance';

interface PerformanceSummaryProps {
  stats: PerformanceStats;
  isLoading?: boolean;
}

interface StatCardProps {
  label: string;
  value: string;
  valueClassName?: string;
}

function StatCard({ label, value, valueClassName = 'text-dark-100' }: StatCardProps) {
  return (
    <div className="bg-dark-800/80 border border-dark-700/50 rounded-xl p-4">
      <p className="text-xs text-dark-400">{label}</p>
      <p className={`text-lg font-semibold ${valueClassName} mt-1`}>{value}</p>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="bg-dark-800/80 border border-dark-700/50 rounded-xl p-4">
      <div className="h-3 w-20 rounded shimmer" />
      <div className="h-6 w-28 rounded shimmer mt-2" />
    </div>
  );
}

export function PerformanceSummary({ stats, isLoading = false }: PerformanceSummaryProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const returnColor = stats.totalReturn >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const returnSign = stats.totalReturn >= 0 ? '+' : '';

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <StatCard
        label="Total Return"
        value={`${returnSign}${formatCurrency(stats.totalReturn)} (${returnSign}${formatPercent(stats.totalReturnPercent)})`}
        valueClassName={returnColor}
      />
      <StatCard label="Start Value" value={formatCurrency(stats.startValue)} />
      <StatCard label="End Value" value={formatCurrency(stats.endValue)} />
      <StatCard label="Max Value" value={formatCurrency(stats.maxValue)} />
      <StatCard label="Min Value" value={formatCurrency(stats.minValue)} />
      <StatCard label="Avg Daily PnL" value={formatCurrency(stats.avgDailyPnl)} />
    </div>
  );
}
